/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { createApi } from '@reduxjs/toolkit/query/react';

import { JimboResponse, WebhookEndpoints, CampaignModal, CustomerCoupons, CouponModel, MultipleCouponModel, Order } from '@models';
import { baseQueryWithReauth } from '@services/refreshToken.service';
import { TalonOneSessionResponse } from '@models/TalonOneSessionResponse';

interface CampaignTotal {
  totalResultSize: number;
  data: CampaignModal[];
}

export const talonOneApi = createApi({
  reducerPath: 'talonOneApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['talonOneApi', 'talonOneCampaign'],
  endpoints: (builder) => ({
    getCampaigns: builder.query<CampaignTotal, void>({
      query: () => `/talon/campaigns`,
      providesTags: ['talonOneApi', 'talonOneCampaign'],
      transformResponse: (response: JimboResponse<CampaignTotal>) => response.data!,
    }),
    getCustomerCoupons: builder.query<CustomerCoupons, { customerId: string }>({
      query: (query) => `/talon/coupons/${query.customerId}`,
      providesTags: ['talonOneApi'],
      transformResponse: (response: JimboResponse<CustomerCoupons>) => response.data!,
    }),
    createCoupons: builder.mutation<WebhookEndpoints, Partial<CouponModel>>({
      query: (coupon) => ({
        url: '/talon/coupons',
        method: 'POST',
        body: coupon,
      }),
      transformResponse: (response: JimboResponse<WebhookEndpoints>) => response.data!,
      invalidatesTags: ['talonOneApi'],
    }),
    createMultipleCoupons: builder.mutation<WebhookEndpoints, Partial<MultipleCouponModel>>({
      query: (coupon) => ({
        url: '/talon/multiple-coupons',
        method: 'POST',
        body: coupon,
      }),
      transformResponse: (response: JimboResponse<WebhookEndpoints>) => response.data!,
      invalidatesTags: ['talonOneApi'],
    }),
    deleteCoupon: builder.mutation<JimboResponse, { campaignId: number; couponId: string }>({
      query: ({ campaignId, couponId }) => ({
        url: `/talon/delete-coupon/${campaignId}/coupons/${couponId}`,
        method: 'DELETE',
        body: { campaignId, couponId },
      }),
      transformResponse: (response: JimboResponse<any>) => response.data!,
      invalidatesTags: ['talonOneApi'],
    }),
    getCouponOrder: builder.query<Order, { couponId: string }>({
      query: (query) => `/talon/coupon-orders/${query.couponId}`,
      providesTags: ['talonOneApi'],
      transformResponse: (response: JimboResponse<Order>) => response.data!,
    }),
    getCustomerSessionData: builder.query<TalonOneSessionResponse, { sessionID: string }>({
      query: (query) => `/talon/session/${query.sessionID}`,
      providesTags: ['talonOneApi'],
      transformResponse: (response: TalonOneSessionResponse) => response,
    }),
  }),
});

export const {
  useGetCampaignsQuery,
  useGetCustomerCouponsQuery,
  useCreateCouponsMutation,
  useCreateMultipleCouponsMutation,
  useDeleteCouponMutation,
  useGetCouponOrderQuery,
  useGetCustomerSessionDataQuery,
} = talonOneApi;
